import { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  adminLongRunningQueriesCollection,
  LongRunningQueryRow,
} from '@/reducers/admin/adminLongRunningQueries'

/**
 * Who may see and use this tool. Mirrors the backend allowlist
 * (settings.LONG_RUNNING_QUERY_ADMIN_EMAILS), which is what actually enforces it —
 * this only decides whether the button is rendered on /admin.
 */
export const LONG_RUNNING_QUERY_ADMIN_EMAILS = ['dev@optiwatt.com']

export function canManageLongRunningQueries(user?: {
  username?: string
}): boolean {
  const email = (user?.username ?? '').toLowerCase()
  return LONG_RUNNING_QUERY_ADMIN_EMAILS.includes(email)
}

const DEFAULT_THRESHOLD_MINUTES = 30
const MIN_THRESHOLD_MINUTES = 20
const MAX_THRESHOLD_MINUTES = 24 * 60

function formatHours(hours: number | null) {
  if (hours === null || hours === undefined) return '—'
  return hours.toFixed(2)
}

function ResultChip({ row }: { row: LongRunningQueryRow }) {
  if (row.action === 'dry_run') {
    return <Chip size="small" label="would cancel" color="warning" />
  }
  if (row.action === 'cancelled' && row.succeeded) {
    return <Chip size="small" label="cancelled" color="success" />
  }
  return (
    <Chip
      size="small"
      label={row.action.startsWith('error') ? row.action : 'not cancelled'}
      color="error"
    />
  )
}

export default function LongRunningQueries() {
  const dispatch = useDispatch()
  const [thresholdMinutes, setThresholdMinutes] = useState(
    DEFAULT_THRESHOLD_MINUTES,
  )
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { createAction, data, isLoading, error } =
    adminLongRunningQueriesCollection.useCreate({
      path: 'admin/long_running_queries',
    })
  const result = data?.[0]
  const rows = result?.rows ?? []

  const thresholdIsValid =
    Number.isInteger(thresholdMinutes) &&
    thresholdMinutes >= MIN_THRESHOLD_MINUTES &&
    thresholdMinutes <= MAX_THRESHOLD_MINUTES

  async function run(dryRun: boolean) {
    setConfirmOpen(false)
    await dispatch(
      createAction({ dry_run: dryRun, threshold_minutes: thresholdMinutes }),
    )
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Long-Running Queries
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Finds Postgres queries that have been active longer than the threshold.
        Preview first; Cancel sends <code>pg_cancel_backend</code> to each one
        (it stops the statement but keeps the connection). pg_cron materialized
        view refreshes are included.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, mb: 2 }}>
        <TextField
          label="Threshold (minutes)"
          type="number"
          size="small"
          value={thresholdMinutes}
          onChange={(e) => setThresholdMinutes(Number(e.target.value))}
          inputProps={{
            min: MIN_THRESHOLD_MINUTES,
            max: MAX_THRESHOLD_MINUTES,
          }}
          error={!thresholdIsValid}
          helperText={
            thresholdIsValid
              ? ''
              : `${MIN_THRESHOLD_MINUTES}–${MAX_THRESHOLD_MINUTES} minutes`
          }
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => run(true)}
          disabled={isLoading || !thresholdIsValid}
        >
          Preview (dry run)
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => setConfirmOpen(true)}
          disabled={isLoading || !thresholdIsValid}
        >
          Cancel long-running queries
        </Button>
        {isLoading && <CircularProgress size={24} />}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Request failed. You may not be allowed to use this tool, or the
          database function is not installed yet.
        </Alert>
      )}

      {result && !isLoading && (
        <>
          <Alert
            severity={
              result.action === 'cancel'
                ? rows.length
                  ? 'warning'
                  : 'success'
                : 'info'
            }
            sx={{ mb: 2 }}
          >
            {result.action === 'cancel' ? 'Cancelled' : 'Dry run'}:{' '}
            {rows.length} quer{rows.length === 1 ? 'y' : 'ies'} active longer
            than {result.threshold_minutes} minutes.
          </Alert>

          {rows.length > 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PID</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Application</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Query</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.pid}>
                      <TableCell>{row.pid}</TableCell>
                      <TableCell>{formatHours(row.hours)}</TableCell>
                      <TableCell>{row.usename ?? '—'}</TableCell>
                      <TableCell>{row.application_name || '—'}</TableCell>
                      <TableCell>
                        <ResultChip row={row} />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          maxWidth: 600,
                        }}
                      >
                        {row.query ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Cancel long-running queries?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This sends pg_cancel_backend to every query active longer than{' '}
            {thresholdMinutes} minutes, including pg_cron materialized view
            refreshes. Run a preview first if you have not already.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Keep running</Button>
          <Button color="error" variant="contained" onClick={() => run(false)}>
            Cancel them
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
